import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";

export const TYPES = [["cdi", "CDI"], ["cdd", "CDD"], ["temps_partiel", "Temps partiel"], ["temps_partage_ge", "Temps partagé GE"]];
export const MOTIFS = [["remplacement", "Remplacement d'un salarié absent"], ["accroissement", "Accroissement temporaire d'activité"],
  ["saisonnier", "Emploi saisonnier"], ["usage", "Contrat d'usage"]];
export const CATEGORIES = [["ouvrier_employe", "Ouvrier / employé"], ["agent_maitrise_technicien", "Agent de maîtrise / technicien"], ["cadre", "Cadre"]];

export function Choice({ value, onChange, options, testid, placeholder }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder || "—"} /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function F({ label, children, wide }) {
  return <div className={`space-y-1 ${wide ? "sm:col-span-2" : ""}`}><Label className="text-xs">{label}</Label>{children}</div>;
}

export function ChampsType({ f, set }) {
  const t = f.type;
  return (<>
    {t === "cdd" && <>
      <F label="Motif du CDD"><Choice value={f.motif_cdd} onChange={set("motif_cdd")} options={MOTIFS} testid="contrat-motif" /></F>
      <F label="Précision du motif"><Input value={f.motif_precision || ""} onChange={set("motif_precision")} data-testid="contrat-motif-precision" /></F>
      {f.motif_cdd === "remplacement" && <F label="Salarié remplacé (nom, qualification)" wide><Input value={f.salarie_remplace || ""} onChange={set("salarie_remplace")} data-testid="contrat-remplace" /></F>}
      <F label="Date de fin"><Input type="date" value={f.date_fin || ""} onChange={set("date_fin")} data-testid="contrat-date-fin" /></F>
    </>}
    {t === "temps_partiel" && <>
      <F label="Répartition (jours / semaines)" wide><Input value={f.repartition_horaire || ""} onChange={set("repartition_horaire")} data-testid="contrat-repartition" /></F>
      <F label="Heures complémentaires (limite)"><Input value={f.heures_complementaires || ""} onChange={set("heures_complementaires")} data-testid="contrat-hc" /></F>
    </>}
  </>);
}

export function ContratCreateDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ type: "temps_partage_ge", categorie: "ouvrier_employe" });
  const [opts, setOpts] = useState({ salaries: [], missions: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/salaries"), api.get("/missions")]).then(([s, m]) => setOpts({ salaries: s.data, missions: m.data })).catch(() => {});
  }, [open]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const submit = async () => {
    setBusy(true);
    try {
      const num = ["duree_hebdo", "salaire_horaire", "periode_essai_jours"];
      const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, num.includes(k) ? Number(v) : v]));
      const { data } = await api.post("/contrats", body, { timeout: 120000 });
      toast.success(`Brouillon ${data.numero} rédigé par ${data.redige_par}`);
      onCreated(data); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  const missions = opts.missions.filter((m) => !f.salarie_id || !m.salarie_id || m.salarie_id === f.salarie_id);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contrat-create-dialog">
        <DialogHeader><DialogTitle>Nouveau contrat — rédaction par ZOE</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Type"><Choice value={f.type} onChange={set("type")} options={TYPES} testid="contrat-type" /></F>
          <F label="Salarié"><Choice value={f.salarie_id} onChange={set("salarie_id")} testid="contrat-salarie" placeholder="Choisir…"
            options={opts.salaries.map((s) => [s.id, `${s.prenom} ${s.nom}${s.poste ? " — " + s.poste : ""}`])} /></F>
          <F label="Mission (pré-remplit dates, lieu, entreprise)" wide><Choice value={f.mission_id} onChange={set("mission_id")} testid="contrat-mission" placeholder="Aucune"
            options={missions.map((m) => [m.id, `${m.libelle} (${m.date_debut} → ${m.date_fin})`])} /></F>
          <F label="Qualification"><Input value={f.qualification || ""} onChange={set("qualification")} data-testid="contrat-qualification" /></F>
          <F label="Catégorie"><Choice value={f.categorie} onChange={set("categorie")} options={CATEGORIES} testid="contrat-categorie" /></F>
          <F label="Durée hebdomadaire (h)"><Input type="number" value={f.duree_hebdo || ""} onChange={set("duree_hebdo")} data-testid="contrat-duree" /></F>
          <F label="Salaire brut horaire (€)"><Input type="number" step="0.01" value={f.salaire_horaire || ""} onChange={set("salaire_horaire")} data-testid="contrat-salaire" /></F>
          <F label="Convention collective"><Input value={f.convention_collective || ""} onChange={set("convention_collective")} data-testid="contrat-ccn" /></F>
          <F label="Période d'essai (jours)"><Input type="number" value={f.periode_essai_jours || ""} onChange={set("periode_essai_jours")} data-testid="contrat-essai" /></F>
          <ChampsType f={f} set={set} />
          <F label="Instructions / clauses particulières pour ZOE" wide>
            <Textarea rows={3} value={f.clauses_particulieres || ""} onChange={set("clauses_particulieres")} data-testid="contrat-clauses" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || !f.salarie_id} data-testid="contrat-create-submit" className="bg-[#17352e] text-white hover:bg-[#284b40]">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />ZOE rédige…</> : <><Sparkles className="h-4 w-4 mr-2" />Rédiger le brouillon</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
